'use client';

import './styles/@theme';
import type { RefObject } from 'react';
import React, { useRef } from 'react';
import { useDraggable } from './hooks/use-draggable.hook';
import { useScrollDetection } from './hooks/use-scroll-detection.hook';
import { classes } from '@/utils/classes/classes';
import { usePagination } from '@/app/elements/draggable-wrapper/hooks/use-pagination.hook';
import { useGlobalContext } from '@/store/contexts/global-context';

export type DraggableProps = {
    indicators?: boolean;
    pagination?: boolean;
    type?: 'container' | 'list';
    children: React.ReactElement[] | React.ReactElement;
    inidicatorClass?: string;
    className?: string;
    observeResize?: boolean;
    onIndicatorClick?: (direction: number) => void;
};

export const DraggableContainer: React.FC<DraggableProps> = ({
    children,
    indicators = true,
    pagination = false,
    type = 'container',
    inidicatorClass,
    className = '',
    observeResize = false,
    onIndicatorClick
}) => {
    const ref: RefObject<HTMLElement | null> = useRef(null);

    const { isNextVisible, isPrevVisible, handleIndicatorClick } = useScrollDetection(ref, { observeResize });
    const { pageCount, dummyWidth, activePage, goToPage, navigateSlideByDirection, cardsPerPage } = usePagination(
        ref,
        children,
        className
    );

    const { isMobile } = useGlobalContext();
    const dragThreshold = isMobile ? 5 : 30;

    const { handleDragStart, handleDragEnd, handleTouchStart } = useDraggable(ref, {
        currentPage: pagination ? activePage : undefined,
        goToPage: pagination ? goToPage : undefined,
        pageCount: pagination ? pageCount : undefined,
        dragThreshold
    });

    let draggableData = {
        pageCount: 0,
        activePage: 0,
        goToPage: (_: number) => {},
        handleIndicatorClick
    };

    if (pagination) {
        draggableData = {
            pageCount,
            activePage,
            goToPage,
            handleIndicatorClick: navigateSlideByDirection
        };
    }

    const onArrowClick = onIndicatorClick ?? draggableData.handleIndicatorClick;

    const isSingleCardCarousel = isMobile && pagination && cardsPerPage === 1;
    const props = {
        onMouseDown: handleDragStart,
        onMouseUp: handleDragEnd,
        onTouchStart: handleTouchStart,
        style: isSingleCardCarousel ? { touchAction: 'pan-y' as const } : undefined
    };

    const content =
        type === 'container' ? (
            <div
                className="draggable-wrapper"
                data-testid="draggable-wrapper"
                ref={ref as RefObject<HTMLDivElement>}
                {...props}>
                {children}
                {pagination && indicators && (
                    <div className="dummy-space" style={{ width: `${dummyWidth}px` }} aria-hidden="true"></div>
                )}
            </div>
        ) : (
            <ul
                className="draggable-wrapper"
                data-testid="draggable-wrapper"
                ref={ref as RefObject<HTMLUListElement>}
                {...props}>
                {children}
                {pagination && indicators && (
                    <li className="dummy-space" style={{ width: `${dummyWidth}px` }} aria-hidden="true"></li>
                )}
            </ul>
        );

    return (
        <div className="draggable-container">
            {indicators && isPrevVisible && (
                <button
                    data-testid="prev-arrow"
                    className={classes('prev-arrow', inidicatorClass)}
                    onClick={() => onArrowClick(-1)}>
                    <div className="indicator"></div>
                </button>
            )}
            {content}
            {indicators && isNextVisible && (
                <button
                    data-testid="next-arrow"
                    className={classes('next-arrow', inidicatorClass)}
                    onClick={() => onArrowClick(1)}>
                    <div className="indicator"></div>
                </button>
            )}
            {pagination && draggableData.pageCount > 1 && (
                <div className="pagination" data-testid="pagination">
                    {Array.from({ length: draggableData.pageCount }).map((_, i) => (
                        <button
                            data-testid="pagination-bullet"
                            key={`page-${i + 1}`}
                            className={
                                i === draggableData.activePage ? 'pagination-bullet-active' : 'pagination-bullet'
                            }
                            onClick={() => draggableData.goToPage(i)}></button>
                    ))}
                </div>
            )}
        </div>
    );
};
